import classNames from 'classnames';
import React, {useContext} from 'react';

import {
    BrowseCourseBadgesContext,
    BrowseCourseBadgeHandler,
    BrowseCourse,
    getCourseBadgeFromType,
    COURSE_IN_SUBS_PLAN_BADGE_FAMILIES,
} from '@udemy/browse-course';
import {
    CourseBadgeFamily,
    DiscoveryItemImpressionEvent,
    discoveryTracker,
} from '@udemy/browse-event-tracking';
import {TrackImpression} from '@udemy/event-tracking';
import {useMatchMedia} from '@udemy/hooks';
import {udPerf} from '@udemy/performance-rum-client';
import {Image} from '@udemy/react-core-components';
import {CourseBuyable, ShoppingItem as UdemyShoppingItem} from '@udemy/shopping';
import {useUDData} from '@udemy/ud-data';

import {Actions} from './actions.react-component';
import {Badges} from './badges.react-component';
import {Instructors} from './instructors.react-component';
import {Meta} from './meta.react-component';
import {Price} from './price.react-component';
import {Ratings} from './ratings.react-component';
import styles from './shopping-item.module.less';
import {ActionHandlers, ActionSupportedLayoutsProps, CartItem} from './types';

const IMAGE_SIZES = {
    mobile: {height: 48, width: 48},
    desktop: {height: 68, width: 120},
};

export interface ShoppingItemProps {
    item: UdemyShoppingItem;
    index: number;
    listName: string;
    performanceMarkName?: string;
    actions: ActionHandlers;
    actionsLayout?: ActionSupportedLayoutsProps;
    usesLatestDiscount?: (item: CartItem) => boolean;
}

export const ShoppingItem: React.FC<ShoppingItemProps> = ({
    item,
    index,
    performanceMarkName,
    usesLatestDiscount = () => false,
    actionsLayout = 'horizontal',
    ...rest
}) => {
    const {buyable, list_price: listPrice, purchase_price: purchasePrice} = item;
    const {Config} = useUDData();
    const isUB = Config.brand.has_organization;
    const isMobile = useMatchMedia('mobile-max');
    const imageSizes = isMobile ? IMAGE_SIZES.mobile : IMAGE_SIZES.desktop;
    const src = (buyable as CourseBuyable).image_240x135;
    const src2x = (buyable as CourseBuyable).image_480x270;
    const {browseBadgeHandlerOptions} = useContext(BrowseCourseBadgesContext);
    const badgeHandler = new BrowseCourseBadgeHandler(
        buyable as unknown as BrowseCourse,
        browseBadgeHandlerOptions,
    );

    function markImageLoadPerformance() {
        udPerf.mark(performanceMarkName as string);
    }

    function trackImpression() {
        discoveryTracker.trackDiscoveryImpression(
            {
                item: {
                    ...item.buyable,
                    visibleBadgeFamilies:
                        badgeHandler.getBadgeFamilies() as unknown as CourseBadgeFamily[],
                },
            },
            {
                backendSource: DiscoveryItemImpressionEvent.backendSourceOptions.SHOPPING_CARTS,
                index,
            },
        );
    }

    const trackClick = () => {
        discoveryTracker.trackDiscoveryItemClickEvent(
            {
                item: {
                    ...item.buyable,
                    visibleBadgeFamilies:
                        badgeHandler.getBadgeFamilies() as unknown as CourseBadgeFamily[],
                },
            },
            {
                backendSource: DiscoveryItemImpressionEvent.backendSourceOptions.SHOPPING_CARTS,
                index,
            },
        );
    };

    let SubsDiffBadge: ReturnType<typeof getCourseBadgeFromType> | null = null;
    // TODO: https://udemyjira.atlassian.net/browse/CVR-151
    // remove the badge handler filtering after experiment concludes; the filter
    // is responsible only for removing badges depending on experiment variant.
    if ((buyable as CourseBuyable).is_in_personal_plan_collection) {
        const subsDiffBadgeType =
            COURSE_IN_SUBS_PLAN_BADGE_FAMILIES.filter((b) => {
                if (browseBadgeHandlerOptions?.filter) {
                    return browseBadgeHandlerOptions.filter(b, buyable as unknown as BrowseCourse);
                }
                return false;
            })[0] ?? null;
        if (subsDiffBadgeType) {
            SubsDiffBadge = getCourseBadgeFromType(subsDiffBadgeType);
        }
    }

    /* eslint-disable react/jsx-no-target-blank */
    /* Since the course card only opens to Udemy-controlled pages (i.e, CLPs), there's
     * no security concern. We want to allow the referrer for tracking purposes.
     */
    const newTabProps = !isMobile ? {target: '_blank' as const, rel: 'noopener'} : {};

    return (
        <TrackImpression trackFunc={trackImpression}>
            <div
                className={classNames(styles.container, {
                    [styles['with-inline-actions']]: actionsLayout === 'vertical',
                })}
            >
                <div className={styles.image}>
                    <div className={styles['image-wrapper']}>
                        <Image
                            className={styles['course-image']}
                            src={src}
                            srcSet={`${src} 1x, ${src2x} 2x`}
                            alt=""
                            height={imageSizes.height}
                            width={imageSizes.width}
                            onLoad={performanceMarkName ? markImageLoadPerformance : undefined}
                        />
                    </div>
                </div>
                <div className={styles.header}>
                    <h3 className={classNames('ud-heading-md', styles['course-title'])}>
                        <a href={buyable.url} onClick={trackClick} {...newTabProps}>
                            {buyable.title}
                        </a>
                    </h3>
                    <Instructors instructors={buyable.visible_instructors ?? []} />
                </div>
                {!isUB && (
                    <Badges
                        buyable={buyable as CourseBuyable}
                        actionsLayout={actionsLayout}
                        {...rest}
                    />
                )}
                <Ratings buyable={buyable as CourseBuyable} />
                <Meta buyable={buyable as CourseBuyable} />
                <Actions item={item} layout={actionsLayout} {...rest} />
                <Price
                    isUB={isUB}
                    buyable={buyable}
                    listPrice={listPrice}
                    purchasePrice={purchasePrice}
                    currentDiscount={item.current_discount}
                    shouldUseLatestDiscountStyles={usesLatestDiscount(item)}
                />
                {SubsDiffBadge && (
                    <div
                        className={classNames(styles['subs-diff-badge-wrapper'], {
                            [styles.hidden]: actionsLayout !== 'vertical',
                        })}
                    >
                        <div className={styles['subs-diff-badge']}>
                            <SubsDiffBadge />
                        </div>
                    </div>
                )}
            </div>
        </TrackImpression>
    );
};
